import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import Spinner from "../generics/Spinner";

const STATUS_OPTIONS = [
  { value: "GOOD", label: "Good" },
  { value: "NEEDS_ATTENTION", label: "Needs Attention" },
  { value: "MAINTENANCE", label: "Under Maintenance" },
  { value: "OUT_OF_SERVICE", label: "Out of Service" },
];

const FacilityForm = ({
  facility,
  facilityTypes,
  locations,
  loading,
  error,
  isEdit,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    facility_type: "",
    location: "",
    status: "GOOD",
    area_size: "",
    notes: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (facility && isEdit) {
      setFormData({
        name: facility.name || "",
        description: facility.description || "",
        facility_type: facility.facility_type || "",
        location: facility.location || "",
        status: facility.status || "GOOD",
        area_size: facility.area_size || "",
        notes: facility.notes || "",
      });
      if (facility.photo) {
        setImagePreview(facility.photo);
      }
    }
  }, [facility, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    const submitData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== "" && formData[key] !== null) {
        submitData.append(key, formData[key]);
      }
    });

    if (imageFile) {
      submitData.append("photo", imageFile);
    } else if (!imagePreview && facility?.photo) {
      submitData.append("photo", "");
    }

    onSubmit(submitData);
  };

  if (loading && isEdit && !facility) {
    return <Spinner />;
  }

  return (
    <div className="facility-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <Link to="/maintenance/facilities" className="text-decoration-none me-2">
            <i className="fa fa-arrow-left"></i>
          </Link>
          <i className="fa fa-building me-2"></i>
          {isEdit ? "Edit Facility" : "New Facility"}
        </h4>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error saving facility"}
        </div>
      )}

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-info-circle me-2"></i>
                Basic Information
              </Card.Header>
              <Card.Body className="pt-4">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter facility name"
                      />
                      <Form.Control.Feedback type="invalid">
                        Name is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Facility Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="facility_type"
                        value={formData.facility_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select type...</option>
                        {facilityTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Facility type is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of the facility"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Location <span className="text-danger">*</span>
                      </Form.Label>
                      <Select
                        options={locations.map((loc) => ({
                          value: loc.id,
                          label: loc.name,
                        }))}
                        value={
                          formData.location
                            ? {
                                value: formData.location,
                                label: locations.find((l) => l.id === formData.location)?.name || "",
                              }
                            : null
                        }
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            location: selected ? selected.value : "",
                          }))
                        }
                        placeholder="Search and select location..."
                        isClearable
                        isSearchable
                        classNamePrefix="react-select"
                      />
                      {validated && !formData.location && (
                        <div className="text-danger small mt-1">
                          Location is required
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-map-marker me-2"></i>
                Location Details
              </Card.Header>
              <Card.Body className="pt-4">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Area Size</Form.Label>
                      <Form.Control
                        type="text"
                        name="area_size"
                        value={formData.area_size}
                        onChange={handleChange}
                        placeholder="e.g., 500 sq ft"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-sticky-note-o me-2"></i>
                Additional Notes
              </Card.Header>
              <Card.Body className="pt-4">
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any additional notes or special instructions..."
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="mb-4">
              <Card.Header className="reports-filter">
                <i className="fa fa-image me-2"></i>
                Facility Image
              </Card.Header>
              <Card.Body className="pt-4">
                {imagePreview ? (
                  <div className="text-center">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="img-fluid mb-2"
                      style={{ maxHeight: "200px" }}
                    />
                    <div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleRemoveImage}
                      >
                        <i className="fa fa-trash me-1"></i> Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fa fa-image fa-3x text-muted mb-3"></i>
                    <p className="text-muted small mb-2">No image uploaded</p>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="d-none"
                      id="facility-image"
                    />
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => document.getElementById("facility-image").click()}
                    >
                      <i className="fa fa-upload me-1"></i> Upload Image
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-2"></i>
                        {isEdit ? "Update Facility" : "Create Facility"}
                      </>
                    )}
                  </Button>
                  <Link
                    to="/maintenance/facilities"
                    className="btn btn-outline-secondary"
                  >
                    <i className="fa fa-times me-2"></i>
                    Cancel
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default FacilityForm;
